import { ArrowLeft, CheckCircle2, CircleDashed, ExternalLink, Loader2, MessageSquareWarning, Rocket, ShieldCheck, Undo2, XCircle } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  STUDIO_TEMPLATE_CATEGORIES,
  adminClient,
  type StudioAudience,
  type StudioTemplate,
  type StudioTemplateCategory,
  type StudioTemplateVersion,
  type StudioTemplateVisibility,
} from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { PAGE_CONTAINER_CLASS, PAGE_TITLE_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminPageLoadError } from '@/components/shell/AdminPageLoadError';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Thumbnail } from '@/components/ui/thumbnail';

import { errorMessage, relativeTime } from '../IntegrationBuilder/builderModel';
import { Field } from '../IntegrationBuilder/builderUi';
import {
  AUDIENCE_OPTIONS,
  TEMPLATE_STATUS_META,
  VERSION_STATUS_META,
  VISIBILITY_OPTIONS,
  actionableVersion,
  needsSummary,
  parseOrgIds,
  parseTags,
  publishBlocker,
} from './templateModel';

function Section({ title, description, actions, children }: { title: string; description?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card/40 p-5">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {description ? <p className="mt-0.5 text-xs text-muted-foreground">{description}</p> : null}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

function CheckIcon({ status }: { status: 'passed' | 'failed' | 'unknown' }) {
  if (status === 'passed') return <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" />;
  if (status === 'failed') return <XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />;
  return <CircleDashed className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
}

function VersionReview({
  template,
  version,
  onChanged,
}: {
  template: StudioTemplate;
  version: StudioTemplateVersion;
  onChanged: () => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'approve' | 'reject' | 'withdraw' | 'publish'>();
  const pending = version.status === 'pending_review';
  const blocker = publishBlocker(template, version);
  const checks = version.source?.quality?.checks ?? [];

  const review = async (decision: 'approved' | 'rejected') => {
    const withdrawing = decision === 'rejected' && version.status === 'approved';
    setBusy(decision === 'approved' ? 'approve' : withdrawing ? 'withdraw' : 'reject');
    try {
      await adminClient.reviewStudioTemplateVersion(template.templateId, version.version, { decision, note: note.trim() });
      toast.success(decision === 'approved' ? `Version ${version.version} approved` : withdrawing ? `Version ${version.version} withdrawn` : 'Sent back to the author');
      setNote('');
      onChanged();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(undefined);
    }
  };

  const publish = async () => {
    setBusy('publish');
    try {
      await adminClient.publishStudioTemplateVersion(template.templateId, version.version);
      toast.success(`Version ${version.version} is live`);
      onChanged();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(undefined);
    }
  };

  return (
    <Section
      title={`Version ${version.version}`}
      description={`Submitted by ${version.submittedBy} ${relativeTime(version.submittedAt)}`}
      actions={<StatusPill tone={VERSION_STATUS_META[version.status].tone}>{VERSION_STATUS_META[version.status].label}</StatusPill>}
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="overflow-hidden rounded-lg border border-border bg-background">
          <div className="flex items-center justify-between border-b border-border px-3 py-1.5 text-[11px] text-muted-foreground">
            <span>Preview of the submitted version</span>
            {version.source?.previewUrl ? (
              <a href={version.source.previewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand">
                Open <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
          </div>
          {version.source?.previewUrl ? (
            <iframe title={`${template.title} v${version.version}`} src={version.source.previewUrl} className="h-[420px] w-full bg-white" sandbox="allow-scripts allow-forms" />
          ) : (
            <Thumbnail
              src={version.source?.thumbnailUrl}
              className="w-full"
              fallback={<div className="flex h-48 items-center justify-center text-xs text-muted-foreground">No preview was saved for this version.</div>}
            />
          )}
        </div>

        <div className="space-y-4 text-sm">
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">Checks</div>
            {checks.length ? (
              <ul className="space-y-1">
                {checks.map((check) => (
                  <li key={check.id} className="flex items-start gap-2 text-xs">
                    <CheckIcon status={check.status} />
                    <span className="min-w-0">
                      <span className="font-mono">{check.id}</span>
                      {check.detail ? <span className="block text-muted-foreground">{check.detail}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">
                {version.source ? 'Passed when it was submitted; no check detail was saved.' : 'The source version no longer exists.'}
              </p>
            )}
          </div>
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">Needs</div>
            <p className="text-xs">{needsSummary(version.needs)}</p>
            {version.needs.integrations.some((need) => need.evidence) ? (
              <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-muted-foreground">
                {version.needs.integrations.map((need) => (need.evidence ? <li key={need.integrationId}>{need.integrationId}: {need.evidence}</li> : null))}
              </ul>
            ) : null}
          </div>
          {version.changelog ? (
            <div>
              <div className="mb-1.5 text-xs font-medium text-muted-foreground">What changed</div>
              <p className="whitespace-pre-wrap text-xs">{version.changelog}</p>
            </div>
          ) : null}
          {version.review ? (
            <div className="rounded-lg border border-border px-3 py-2 text-xs">
              <span className="font-medium">{version.review.decision === 'approved' ? 'Approved' : 'Changes requested'}</span> by {version.review.reviewedBy}{' '}
              {relativeTime(version.review.reviewedAt)}
              {version.review.note ? <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{version.review.note}</p> : null}
            </div>
          ) : null}
        </div>
      </div>

      {pending ? (
        <div className="mt-5 space-y-3 border-t border-border pt-4">
          <Field id="review-note" label="Note for the author" hint="Required to send it back. The author sees it in Studio, under Manage → Settings → Template." optional>
            <Textarea id="review-note" value={note} onChange={(event) => setNote(event.target.value)} className="min-h-20 text-sm" maxLength={2000} />
          </Field>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <span className="mr-auto text-xs text-muted-foreground">Approving copies this version into template storage.</span>
            <Button variant="outline" onClick={() => void review('rejected')} disabled={Boolean(busy) || !note.trim()}>
              {busy === 'reject' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessageSquareWarning className="mr-2 h-4 w-4" />}
              Request changes
            </Button>
            <Button onClick={() => void review('approved')} disabled={Boolean(busy)}>
              {busy === 'approve' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
              Approve
            </Button>
          </div>
        </div>
      ) : null}

      {version.status === 'approved' ? (
        <div className="mt-5 space-y-3 border-t border-border pt-4">
          <Field
            id="withdraw-note"
            label="Note for the author"
            hint="Required to withdraw. Withdrawing sends this version back so it never goes live, and lets the author delete the source app."
            optional
          >
            <Textarea id="withdraw-note" value={note} onChange={(event) => setNote(event.target.value)} className="min-h-20 text-sm" maxLength={2000} />
          </Field>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <span className="mr-auto text-xs text-muted-foreground">
              {blocker ?? (template.latestPublishedVersion ? `Replaces v${template.latestPublishedVersion} for new copies. Existing copies keep working.` : 'Customers will see it in Templates.')}
            </span>
            <Button variant="outline" onClick={() => void review('rejected')} disabled={Boolean(busy) || !note.trim()}>
              {busy === 'withdraw' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />}
              Withdraw v{version.version}
            </Button>
            <Button onClick={() => void publish()} disabled={Boolean(busy) || Boolean(blocker)}>
              {busy === 'publish' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Rocket className="mr-2 h-4 w-4" />}
              Publish v{version.version}
            </Button>
          </div>
        </div>
      ) : null}
    </Section>
  );
}

function ListingEditor({ template, onSaved }: { template: StudioTemplate; onSaved: (template: StudioTemplate) => void }) {
  const [title, setTitle] = useState(template.title);
  const [summary, setSummary] = useState(template.summary);
  const [description, setDescription] = useState(template.description);
  const [category, setCategory] = useState<StudioTemplateCategory>(template.category);
  const [tags, setTags] = useState(template.tags.join(', '));
  const [visibility, setVisibility] = useState<StudioTemplateVisibility>(template.visibility);
  const [orgIds, setOrgIds] = useState(template.allowedOrgIds.join('\n'));
  const [audience, setAudience] = useState<StudioAudience>(template.audience ?? 'everyone');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { template: saved } = await adminClient.updateStudioTemplate(template.templateId, {
        title: title.trim(),
        summary: summary.trim(),
        description: description.trim(),
        category,
        tags: parseTags(tags),
        visibility,
        allowedOrgIds: parseOrgIds(orgIds),
        audience,
      });
      toast.success('Listing saved');
      onSaved(saved);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Listing" description="What customers see in Templates before they use it.">
      <div className="grid gap-4 md:grid-cols-2">
        <Field id="listing-title" label="Title">
          <Input id="listing-title" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={80} />
        </Field>
        <Field label="Category">
          <Select value={category} onValueChange={(value) => setCategory(value as StudioTemplateCategory)}>
            <SelectTrigger aria-label="Category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STUDIO_TEMPLATE_CATEGORIES.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field id="listing-summary" label="Summary" hint="One line on the card. Required to publish." className="md:col-span-2">
          <Input id="listing-summary" value={summary} onChange={(event) => setSummary(event.target.value)} maxLength={200} />
        </Field>
        <Field id="listing-description" label="Description" optional className="md:col-span-2">
          <Textarea id="listing-description" value={description} onChange={(event) => setDescription(event.target.value)} className="min-h-24 text-sm" maxLength={4000} />
        </Field>
        <Field id="listing-tags" label="Tags" hint="Comma-separated, up to 10." optional>
          <Input id="listing-tags" value={tags} onChange={(event) => setTags(event.target.value)} />
        </Field>
        <Field label="Who can see it" hint={VISIBILITY_OPTIONS.find((option) => option.id === visibility)?.hint}>
          <Select value={visibility} onValueChange={(value) => setVisibility(value as StudioTemplateVisibility)}>
            <SelectTrigger aria-label="Who can see it">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VISIBILITY_OPTIONS.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Audience" hint={AUDIENCE_OPTIONS.find((option) => option.id === audience)?.hint}>
          <Select value={audience} onValueChange={(value) => setAudience(value as StudioAudience)}>
            <SelectTrigger aria-label="Audience">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AUDIENCE_OPTIONS.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {visibility === 'org_list' ? (
          <Field id="listing-orgs" label="Organizations" hint="One org_… id per line." className="md:col-span-2">
            <Textarea id="listing-orgs" value={orgIds} onChange={(event) => setOrgIds(event.target.value)} className="min-h-20 font-mono text-xs" />
          </Field>
        ) : null}
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={() => void save()} disabled={saving || !title.trim()}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save listing
        </Button>
      </div>
    </Section>
  );
}

export function TemplateDetailPage() {
  const { templateId = '' } = useParams();
  const [template, setTemplate] = useState<StudioTemplate>();
  const [versions, setVersions] = useState<StudioTemplateVersion[]>([]);
  const [error, setError] = useState<string>();
  const [retiring, setRetiring] = useState(false);

  const load = useCallback(() => {
    setError(undefined);
    adminClient
      .getStudioTemplate(templateId)
      .then((response) => {
        setTemplate(response.template);
        setVersions(response.versions);
      })
      .catch((failure) => {
        setError(errorMessage(failure));
        toast.error(errorMessage(failure));
      });
  }, [templateId]);

  useEffect(load, [load]);

  const setRetired = async (retired: boolean) => {
    if (!template) return;
    setRetiring(true);
    try {
      const response = await adminClient.setStudioTemplateRetired(template.templateId, retired);
      setTemplate(response.template);
      toast.success(retired ? 'Retired. Existing copies keep working.' : 'Back in the gallery');
    } catch (failure) {
      toast.error(errorMessage(failure));
    } finally {
      setRetiring(false);
    }
  };

  if (error && !template) {
    return <AdminPageLoadError message={error} onRetry={load} backTo="/templates" backLabel="Back to templates" />;
  }
  if (!template) {
    return (
      <div className={PAGE_CONTAINER_CLASS}>
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading template…
        </div>
      </div>
    );
  }

  const current = actionableVersion(versions);
  const status = TEMPLATE_STATUS_META[template.status];

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <Link to="/templates" className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" />
        Templates
      </Link>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className={PAGE_TITLE_CLASS}>{template.title}</h1>
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {template.latestPublishedVersion ? `v${template.latestPublishedVersion} live · ` : ''}
            Used {template.usageCount} {template.usageCount === 1 ? 'time' : 'times'} · <span className="font-mono text-xs">{template.slug}</span>
          </p>
        </div>
        {template.status === 'published' ? (
          <Button variant="outline" onClick={() => void setRetired(true)} disabled={retiring}>
            Retire
          </Button>
        ) : template.status === 'retired' ? (
          <Button variant="outline" onClick={() => void setRetired(false)} disabled={retiring}>
            Put back in the gallery
          </Button>
        ) : null}
      </div>

      <div className="space-y-6">
        {current ? <VersionReview key={`${current.version}-${current.status}`} template={template} version={current} onChanged={load} /> : null}
        <ListingEditor key={template.updatedAt} template={template} onSaved={setTemplate} />
        <Section title="Versions" description="Every version keeps its source. A live version never changes; a new one replaces it for new copies.">
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-card/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">Version</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="hidden px-4 py-2 font-medium md:table-cell">Needs</th>
                  <th className="px-4 py-2 text-right font-medium">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((version) => (
                  <tr key={version.version} className="border-t border-border align-top">
                    <td className="px-4 py-2.5 font-mono text-xs">v{version.version}</td>
                    <td className="px-4 py-2.5">
                      <StatusPill tone={VERSION_STATUS_META[version.status].tone}>{VERSION_STATUS_META[version.status].label}</StatusPill>
                      {version.review?.note ? <p className="mt-1 max-w-sm text-[11px] text-muted-foreground">{version.review.note}</p> : null}
                    </td>
                    <td className="hidden px-4 py-2.5 text-xs md:table-cell">{needsSummary(version.needs)}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-muted-foreground">
                      {relativeTime(version.submittedAt)}
                      <div className="truncate text-[11px]">{version.submittedBy}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
}
